import { useEffect, useState } from 'react';

type Choice = 'system' | 'light' | 'dark';
const KEY = 'kr.theme';

const read = (): Choice => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
};

const apply = (c: Choice) => {
  // Light unless asked otherwise. "system" is written out as a real
  // value rather than an absent attribute, so a dark laptop cannot
  // silently decide what a client's staff see.
  document.documentElement.setAttribute(
    'data-theme', c === 'system' ? 'auto' : c,
  );
};


/**
 * Light and dark, with the system setting as the starting point.
 *
 * Two states in the button rather than three: nobody wants to cycle
 * through "system" to get back to what they were looking at. Choosing
 * either one sticks, and clearing it is not worth a control.
 */
export const ThemeToggle = () => {
  const [choice, setChoice] = useState<Choice>(read);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  const dark = choice === 'dark';

  const flip = () => {
    const next: Choice = dark ? 'light' : 'dark';
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // Private browsing refuses storage; the choice still applies
      // for this session, which is the part that matters.
    }
    setChoice(next);
  };

  return (
    <button
      className="iconBtn"
      onClick={flip}
      title={dark ? 'Switch to light' : 'Switch to dark'}
      aria-label={dark ? 'Switch to light' : 'Switch to dark'}
    >
      {dark ? (
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
             stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M2 12h2M20 12h2" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
             stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
        </svg>
      )}
    </button>
  );
};
